import React, { useEffect, useMemo, useState } from 'react';
import ProgramCreationView from '../../views/ver_24.10/ProgramCreation.view';
import { CHAT_TYPE, LOCAL_STORAGE_KEYS } from "../../utils/constants";
import { useHistory, useLocation } from "react-router-dom";
import Loader from "../../ui_library/ver_24.10/Loader/Loader.comp";
import {
    createProgramService,
    fetchProgramService,
    CurationPromptService,
    CurationPromptStreamService
} from "../../services/program.service";

const ProgramCreationPage = () => {
    const history = useHistory();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const programId = queryParams.get('programId');
    const [chatHistory, setChatHistory] = useState([]);
    const [showLoader, setShowLoader] = useState(false);
    const [isBotThinking, setIsBotThinking] = useState(false);
    const [programIdState, setProgramIdState] = useState(programId || null);

    useEffect(() => {
        setShowLoader(true);
        initializeChatHistory()
            .then(() => setShowLoader(false))
            .catch(() => setShowLoader(false));
    }, [programIdState]);

    const initializeChatHistory = async () => {
        if (programIdState) {
            await fetchChatHistory(programIdState);
        }
    };

    const fetchChatHistory = async (programId) => {
        if (!programId) return;
        try {
            const response = await fetchProgramService(programId);
            if (response.data && response.data.existingProgram) {
                console.log('program_fetched: ', response.data.existingProgram);
                if (response.data.allMsgs) {
                    const formattedMessages = response.data.allMsgs.map(msg => ({
                        type: msg.sender_type,
                        text: msg.message
                    }));
                    setChatHistory(prev => [...prev, ...formattedMessages]);
                }
            }
        } catch (e) {
            // already toasted the error
        }
    };

    const data = useMemo(() => ({
        programTitle: 'Digital Literacy Program',
        chats: chatHistory
    }), [chatHistory]);


    const options = {
        height: '65vh', //small, medium, large sm/md/lg
        chatType: 'programCreation',
        showChatbotLoader: isBotThinking
    };

    const actions = {
        sendMessageAction: async (user_message) => {
            if (user_message === 'clear') {
                localStorage.clear();
                window.location.reload();
            } else {
                // Append the user's message
                setChatHistory(prev => [
                    ...prev,
                    { type: CHAT_TYPE.USER, text: user_message },
                ]);
                let currentProgramId = programIdState;

                // Create program if not available
                if (!currentProgramId) {
                    setShowLoader(true);
                    try {
                        const result = await createProgramService();
                        if (result && result.data) {
                            currentProgramId = result.data.program_id;
                            setProgramIdState(currentProgramId);
                        }
                    } catch (e) {
                        console.error('Error creating program:', e);
                    }
                    setShowLoader(false);
                }

                // Check if we need to perform the non-streaming "create program" action
                if (user_message === 'ok, create program') {
                    try {
                        const result = await CurationPromptService(currentProgramId, user_message);
                        if (result.data && result.data.redirect_to_outline) {
                            history.push(`/program/${currentProgramId}/dashboard`);
                        }
                        // Optionally update chat history based on JSON response here.
                    } catch (e) {
                        console.error('Error in non-streaming service:', e);
                    }
                } else {
                    // Streaming response
                    setIsBotThinking(true);
                    try {
                        const response = await CurationPromptStreamService(currentProgramId, user_message);
                        if (!response.ok) {
                            throw new Error('Network response was not ok');
                        }
                        if (!response.body) {
                            throw new Error('Readable stream not supported in your browser.');
                        }

                        const reader = response.body.getReader();
                        const decoder = new TextDecoder('utf-8');
                        let botMessage = ''; // Accumulate the message content
                        let doneStreaming = false;

                        while (!doneStreaming) {
                            const { done, value } = await reader.read();
                            if (done) break;
                            const chunk = decoder.decode(value);
                            const lines = chunk.split('data: ');
                            for (let line of lines) {
                                const content = line
                                if (content === '[DONE]') {
                                    setIsBotThinking(false);
                                    doneStreaming = true;
                                    break;
                                } else {
                                    botMessage += content;

                                    // Check if the message contains the redirect marker
                                    if (botMessage.includes("{{redirect_to_outline=true}}")) {
                                        // Remove the marker from the message
                                        botMessage = botMessage.replace("{{redirect_to_outline=true}}", "");
                                        // Redirect to the program dashboard
                                        history.push(`/program/${currentProgramId}/dashboard`);
                                    }

                                    // Update the chat history: update last bot message if exists, otherwise add new.
                                    setChatHistory(prev => {
                                        if (prev.length > 0 && prev[prev.length - 1].type === CHAT_TYPE.BOT) {
                                            const updated = [...prev];
                                            updated[updated.length - 1].text = botMessage;
                                            return updated;
                                        } else {
                                            return [...prev, { type: CHAT_TYPE.BOT, text: botMessage }];
                                        }
                                    });
                                }
                            }
                        }
                        // After stream, check for redirect header (if still used in your case)
                        const redirectFlag = response.headers.get('redirect_to_outline');
                        if (redirectFlag && currentProgramId) {
                            history.push(`/program/${currentProgramId}/dashboard`);
                        }
                        setIsBotThinking(false);
                    } catch (error) {
                        console.error('Streaming error:', error);
                        setIsBotThinking(false);
                        // Optionally show error feedback
                    }
                }
            }
        }
    };

    if (showLoader) {
        return <Loader />;
    }
    return (
        <div>
            <ProgramCreationView
                data={data}
                actions={actions}
                options={options}
            />
        </div>
    );
};

export default ProgramCreationPage;